import React from 'react';
import { CheckCircle, ChevronRight, Pen, RefreshCw, X } from 'lucide-react';

// Extracted from EmployerDashboard.js — receives everything it needs via `ctx`.
export default function EmployerTimeEditRequestsModal({ ctx }) {
  const { handleApproveTimeEdit, handleRejectTimeEdit, loadTimeEditRequests, setShowTimeEditRequests, showTimeEditRequests, timeEditRequests, timeEditRequestsLoading } = ctx;
  return (
    <>
    {showTimeEditRequests && (
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-100 to-orange-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-200 backdrop-blur-sm rounded-xl">
                <Pen className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-amber-800">Time Edit Requests</h2>
                <p className="text-sm text-amber-700/80">Review and approve worker time adjustments</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadTimeEditRequests}
                disabled={timeEditRequestsLoading}
                className="p-2 bg-amber-200 hover:bg-amber-300 rounded-lg transition"
                title="Refresh"
                data-testid="refresh-time-edit-requests"
              >
                <RefreshCw className={`w-5 h-5 text-amber-700 ${timeEditRequestsLoading ? 'animate-spin' : ''}`} />
              </button>
              <button 
                onClick={() => setShowTimeEditRequests(false)}
                className="p-2 hover:bg-amber-200 rounded-lg transition"
                data-testid="close-time-edit-requests-modal"
              >
                <X className="w-5 h-5 text-amber-700" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {timeEditRequestsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : timeEditRequests.filter(r => r.status === 'pending').length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex p-4 bg-slate-100 rounded-2xl mb-4">
                  <Pen className="w-12 h-12 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Pending Time Edit Requests</h3>
                <p className="text-slate-500">Worker time adjustment requests will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {timeEditRequests.filter(r => r.status === 'pending').map((request) => (
                  <div 
                    key={request._id} 
                    className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:shadow-md transition"
                    data-testid={`time-edit-request-${request._id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Worker Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                          {request.worker_name?.charAt(0)?.toUpperCase() || 'W'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 text-lg">{request.worker_name || 'Worker'}</h4>
                          <p className="text-sm text-slate-600 font-medium">{request.job_title}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Requested {request.created_at && new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      {/* Time Changes */}
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Requested Changes</p>
                        <div className="space-y-2 text-sm">
                          {request.requested_checkin_time && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Check-in:</span>
                              <span className="text-red-500 line-through">
                                {request.original_checkin_time && new Date(request.original_checkin_time).toLocaleTimeString()}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                              <span className="text-emerald-600 font-semibold">
                                {new Date(request.requested_checkin_time).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                          {request.requested_checkout_time && (
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">Check-out:</span>
                              <span className="text-red-500 line-through">
                                {request.original_checkout_time && new Date(request.original_checkout_time).toLocaleTimeString()}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                              <span className="text-emerald-600 font-semibold">
                                {new Date(request.requested_checkout_time).toLocaleTimeString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Reason */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Reason for Request:</p>
                      <p className="text-sm text-slate-700">{request.reason}</p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-slate-200">
                      <button
                        onClick={() => {
                          const reason = window.prompt('Enter rejection reason (optional):');
                          handleRejectTimeEdit(request._id, reason);
                        }}
                        className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition text-sm font-medium"
                        data-testid={`reject-time-edit-${request._id}`}
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleApproveTimeEdit(request._id)}
                        className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:shadow-lg transition text-sm font-medium flex items-center gap-2"
                        data-testid={`approve-time-edit-${request._id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
